import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ListFilter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getStickyHeaderBottom } from '../../utils/stickyHeader';

interface ColumnFilterDropdownProps {
  active: boolean;
  label?: string;
  onReset?: () => void;
  children: ReactNode;
}

/**
 * Bouton de filtre de colonne (icône ListFilter) ouvrant un panneau déroulant
 * flottant et agnostique au contenu :
 * - rendu via portail en position fixed, suit le défilement,
 * - s'arrête sous la barre du haut pour rester visible,
 * - ne dépasse pas les bords du viewport (clamp + max-height),
 * - se ferme au clic extérieur ou sur Échap.
 */
export default function ColumnFilterDropdown({
  active,
  label,
  onReset,
  children,
}: ColumnFilterDropdownProps) {
  const { t } = useTranslation('operations');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number }>({
    top: 0,
    left: 0,
    maxHeight: 0,
  });

  const recompute = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    const headerBottom = getStickyHeaderBottom(56);
    const pw = panelRef.current?.offsetWidth ?? 240;

    // Sous l'icône par défaut, mais jamais au-dessus de la barre du haut.
    const top = Math.max(headerBottom + margin, r.bottom + 4);
    // Aligné sur l'icône, clampé aux bords gauche/droit.
    let left = Math.min(r.left, vw - pw - margin);
    left = Math.max(margin, left);
    // Hauteur disponible jusqu'au bas du viewport.
    const maxHeight = vh - top - margin;

    setPos({ top, left, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (open) recompute();
  }, [open, recompute]);

  // Recalcul sur scroll / resize.
  useEffect(() => {
    if (!open) return;
    const handler = () => recompute();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, recompute]);

  // Fermeture au clic extérieur / Échap.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // Ignorer les clics dans le calendrier de DateInput (portalisé sur body).
      if (target instanceof Element && target.closest('[data-dateinput-calendar="true"]')) return;
      // Ignorer les clics dans le dropdown du sélecteur de catégorie (portalisé).
      if (target instanceof Element && target.closest('[data-category-dropdown="true"]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={label || t('filters.filter')}
        aria-label={label || t('filters.filter')}
        className={`relative p-0.5 rounded transition-colors ${
          active || open ? 'text-primary-600' : 'text-gray-300 hover:text-gray-600'
        }`}
      >
        <ListFilter className="w-3.5 h-3.5" />
        {active && (
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-primary-500" />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm font-normal text-gray-700 text-left"
            style={{
              top: pos.top,
              left: pos.left,
              maxHeight: pos.maxHeight || undefined,
              overflowY: 'auto',
              minWidth: 220,
              maxWidth: 'calc(100vw - 16px)',
            }}
          >
            <div className="p-3 space-y-3">
              {children}
              {onReset && active && (
                <div className="pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={onReset}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {t('filters.reset')}
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
